import { faker } from "@faker-js/faker";

import { fakeIllustrationName } from "@/__mocks__/mockBaseTypes";
import { merge } from "@/__mocks__/mockHelpers";
import type {
  RecommendedGroupClass,
  RecommendedGroupClassInstance,
} from "@/store/api/schedule/groupClasses";
import type { PatientRecommendation } from "@/utils/recommendedGroupClass/modalVisibility.util";

const fakeRecommendedGroupClass = (
  overrides?: DeepPartial<RecommendedGroupClass>,
): RecommendedGroupClass => {
  const recommendedGroupClass: RecommendedGroupClass = {
    description: faker.lorem.sentence(),
    iconName: fakeIllustrationName(),
    instances: [fakeRecommendedGroupClassInstance()],
    lengthInWeeks: faker.number.int({ max: 52, min: 1 }),
    title: faker.lorem.words(3),
  };

  return merge(recommendedGroupClass, overrides);
};

const fakeRecommendedGroupClassInstance = (
  overrides?: DeepPartial<RecommendedGroupClassInstance>,
): RecommendedGroupClassInstance => {
  const instance: RecommendedGroupClassInstance = {
    endTime: faker.date.soon().toISOString(),
    groupClassExternalId: faker.string.uuid(),
    startTime: faker.date.soon().toISOString(),
  };

  return merge(instance, overrides);
};

const fakeRecommendedGroupInstance = (
  overrides?: DeepPartial<RecommendedGroupInstance>,
): RecommendedGroupInstance => {
  const instance: RecommendedGroupInstance = {
    endTime: faker.date.soon().toISOString(),
    groupSectionEventId: faker.string.uuid(),
    groupSectionId: faker.string.uuid(),
    startTime: faker.date.soon().toISOString(),
  };

  return merge(instance, overrides);
};

const fakeRecommendedGroup = (
  overrides?: DeepPartial<RecommendedGroup>,
): RecommendedGroup => {
  const group: RecommendedGroup = {
    description: faker.lorem.sentence(),
    groupId: faker.string.uuid(),
    groupTypeCode: faker.word.words(1),
    iconName: fakeIllustrationName(),
    instances: [fakeRecommendedGroupInstance()],
    isCore: faker.datatype.boolean(),
    isRegistered: false,
    isSensitive: faker.datatype.boolean(),
    lengthInWeeks: faker.number.int({ max: 52, min: 1 }),
    title: faker.lorem.words(3),
  };

  return merge(group, overrides);
};

const fakePatientRecommendation = (
  overrides?: DeepPartial<PatientRecommendation>,
): PatientRecommendation => {
  const recommendation: PatientRecommendation = {
    patientExternalId: faker.string.uuid(),
    recommendedGroupClass: fakeRecommendedGroupClass(),
  };
  return merge(recommendation, overrides);
};

const fakePatientRecommendationForClass = ({
  classId,
  patientId,
}: {
  classId: string;
  patientId: string;
}): PatientRecommendation =>
  fakePatientRecommendation({
    patientExternalId: patientId,
    recommendedGroupClass: fakeRecommendedGroupClass({
      instances: [
        fakeRecommendedGroupClassInstance({ groupClassExternalId: classId }),
      ],
    }),
  });

export {
  fakePatientRecommendation,
  fakePatientRecommendationForClass,
  fakeRecommendedGroup,
  fakeRecommendedGroupClass,
  fakeRecommendedGroupClassInstance,
  fakeRecommendedGroupInstance,
};
